'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, LogIn, Mail } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

export default function LoginForm() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    defaultValues: { remember: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Bienvenido a PrestaMass');
      router.push('/');
      router.refresh();
    } catch (error: any) {
      const msg = error?.message || '';
      if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        toast.error('Correo o contraseña incorrectos.');
      } else if (msg.includes('Email not confirmed')) {
        toast.error('Confirma tu correo electrónico antes de iniciar sesión.');
      } else if (msg.includes('Too many requests')) {
        toast.error('Demasiados intentos. Espera unos minutos.');
      } else {
        toast.error('Error al iniciar sesión. Intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-800 text-foreground">Bienvenido</h1>
        <p className="text-sm text-muted-foreground mt-1">Ingresa a tu cuenta de PrestaMass</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Correo electrónico</label>
          <input
            type="email"
            placeholder="correo@ejemplo.com"
            {...register('email', {
              required: 'El correo es obligatorio',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Correo inválido' },
            })}
            className={`w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground ${
              errors.email ? 'border-danger bg-danger-bg' : 'border-border focus:border-primary/60'
            }`}
          />
          {errors.email && (
            <p className="text-danger text-xs mt-1.5">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-600 text-foreground">Contraseña</label>
            <Link href="/auth/forgot-password" className="text-xs text-primary hover:text-primary/80 font-500 transition-colors">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••••"
              {...register('password', { required: 'La contraseña es obligatoria' })}
              className={`w-full px-4 py-2.5 pr-11 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground ${
                errors.password ? 'border-danger bg-danger-bg' : 'border-border focus:border-primary/60'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-danger text-xs mt-1.5">{errors.password.message}</p>
          )}
        </div>

        {/* Remember */}
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            {...register('remember')}
            className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30 cursor-pointer"
          />
          <span className="text-sm text-muted-foreground">Recordarme en este dispositivo</span>
        </label>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white font-600 rounded-xl hover:bg-primary/90 transition-all active:scale-[0.98] disabled:opacity-60 text-sm"
        >
          {loading ? (
            <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Ingresando...</>
          ) : (
            <><LogIn size={16} />Iniciar sesión</>
          )}
        </button>
      </form>

      {/* Forgot password link */}
      <div className="text-center">
        <Link href="/auth/forgot-password" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors">
          <Mail size={14} />
          ¿Olvidaste tu contraseña?
        </Link>
      </div>
    </div>
  );
}